import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Activity, Rocket, Sparkles, TrendingUp, ShieldCheck, Code2 } from "lucide-react";
import PlatformLinks from "@/components/PlatformLinks";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { SERVICES } from "@/data/content";

const ICONS = { Activity, Rocket, Sparkles, TrendingUp, ShieldCheck, Code2 };

export default function Services() {
  return (
    <>
      <SEO
        title="Services — Account Management, Onboarding, Listing, Ads & More"
        description="Six specialised practices: Marketplace account management, seller onboarding, listing optimization, ads & growth, account health & compliance, and website & app development."
        path="/services"
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Services</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            One team. Every step from <span className="bg-black px-2 text-[#FFD700]">GST to GMV.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-black/80 md:text-lg">
            Six specialised practices designed for India's marketplace landscape. Plug them in individually or run them all under one accountable team.
          </p>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl space-y-0 px-4 md:px-8">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Activity;
            const isAlt = i % 2 === 1;
            return (
              <div
                key={s.id}
                data-testid={`service-detail-${s.id}`}
                className={`grid grid-cols-1 items-stretch border-2 border-black md:grid-cols-12 ${
                  i > 0 ? "-mt-[2px]" : ""
                }`}
              >
                <div
                  className={`flex flex-col justify-center p-8 md:col-span-5 md:p-12 ${
                    isAlt ? "bg-black text-white order-2 md:order-1" : "bg-[#FFD700] order-1"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`flex h-14 w-14 items-center justify-center border-2 ${
                        isAlt ? "border-[#FFD700] bg-[#FFD700] text-black" : "border-black bg-black text-[#FFD700]"
                      }`}
                    >
                      <Icon size={26} />
                    </div>
                    <div className={`text-xs font-bold uppercase tracking-widest ${isAlt ? "text-[#FFD700]" : "text-black/60"}`}>
                      0{i + 1} / 0{SERVICES.length}
                    </div>
                  </div>
                  <h2 className="display mt-6 text-3xl font-black tracking-tight md:text-4xl">{s.title}</h2>
                  <p className={`mt-3 text-sm md:text-base ${isAlt ? "text-white/80" : "text-black/80"}`}>{s.short}</p>
                </div>

                <div className={`flex flex-col p-8 md:col-span-7 md:p-12 ${isAlt ? "order-1 md:order-2 bg-white" : "order-2 bg-white"}`}>
                  <p className="text-base leading-relaxed text-black/80 md:text-lg">{s.full}</p>
                  <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-black" /> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link to="/contact/?type=expert" className="btn-brutal" data-testid={`service-cta-${s.id}`}>
                      Talk to a {s.title.split(" ")[0]} expert <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <PlatformLinks extra title="Services by platform and market" />

      <CtaBanner
        title="Not sure which service you need?"
        subtitle="Book a free 30-minute audit. We'll tell you what's broken, what's missing, and what to build first."
      />
    </>
  );
}
